/* iter-studio-dashboard — Studio Production Dashboard.
 *
 * Premier-tier "factory floor" view. Three cards + the shared prompt
 * quality checklist:
 *   • Volume throughput — completed novels/day + sparkline
 *   • Voice consistency — avg preflight score across the shelf
 *   • Prompt completion — overall rate + weakest fields
 *
 * Rendered as a full-screen Dialog opened from the MenuBar. Pulls all
 * numbers from `/api/studio-dashboard/me` (30-day default window).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Factory, TrendingUp, ShieldCheck, ListChecks, Loader2,
  BookText, AlertTriangle, Sparkles,
} from 'lucide-react';
import PromptQualityChecklist from './PromptQualityChecklist';
import { HelpTip } from '@/components/HelpTip';   // iter-p2-e

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const OPEN_EVENT = 'studio-dashboard-open';

export function openStudioDashboard() {
  try { window.dispatchEvent(new CustomEvent(OPEN_EVENT)); } catch { /* ignore */ }
}

function Sparkline({ series }) {
  if (!series || series.length === 0) return null;
  const w = 220, h = 40, pad = 4;
  const max = Math.max(1, ...series.map((s) => s.count));
  const step = (w - pad * 2) / Math.max(1, series.length - 1);
  const points = series.map((s, i) => {
    const x = pad + i * step;
    const y = h - pad - ((s.count / max) * (h - pad * 2));
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-10 w-full" data-testid="studio-throughput-sparkline">
      <polyline
        fill="none" stroke="currentColor" strokeWidth="1.6"
        points={points}
        className="text-oxblood-600"
      />
    </svg>
  );
}

function Card({ icon: Icon, title, primary, subtitle, tone = 'default', testId, children }) {
  const toneClass = {
    default: 'border-border bg-card',
    ok:      'border-emerald-500/40 bg-emerald-500/5',
    warn:    'border-amber-500/50 bg-amber-500/5',
  }[tone] || 'border-border bg-card';
  return (
    <div className={`flex flex-col rounded-md border ${toneClass} p-3`} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" />
        {title}
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="font-mono-stat text-[24px] font-bold text-foreground"
              data-testid={`${testId}-primary`}>
          {primary}
        </span>
        {subtitle && (
          <span className="text-[11px] text-muted-foreground">{subtitle}</span>
        )}
      </div>
      {children}
    </div>
  );
}

export default function StudioProductionDashboard() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setL] = useState(false);

  useEffect(() => {
    const onOpen = () => { setOpen(true); };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setL(true);
    axios.get(`${API}/studio-dashboard/me`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setL(false); });
    return () => { alive = false; };
  }, [open]);

  const bulk    = data?.bulk_throughput || {};
  const voice   = data?.voice_consistency || {};
  const prompt  = data?.prompt_completion || {};

  const voiceTone = voice.avg_score >= 80 ? 'ok'
    : voice.avg_score >= 60 || voice.sample_size === 0 ? 'default'
    : 'warn';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-h-[90vh] max-w-5xl overflow-hidden bg-white p-0"
        data-testid="studio-dashboard-dialog"
      >
        <DialogHeader className="border-b bg-gradient-to-r from-oxblood-50 to-amber-50 px-5 py-3.5">
          <DialogTitle className="flex items-center gap-2 text-[16px]"
                       style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            <Factory className="h-5 w-5 text-oxblood-700" />
            Studio Production Dashboard
            <span className="ml-2 rounded-full bg-oxblood-700 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-white">
              Premier
            </span>
            <HelpTip
              slug="studio-dashboard"
              title="Studio Production Dashboard"
              content={<>Your last 30 days as a P&amp;L: <b>throughput</b> (completed novels/day), <b>voice consistency</b> (avg preflight score across the shelf), and <b>prompt completion</b> (which high-impact bible fields you routinely leave blank). The pill in the desk header is the weighted composite — hover it for the breakdown.</>}
            >
              <span data-testid="studio-dashboard-help-anchor" className="sr-only">studio-dashboard-help</span>
            </HelpTip>
          </DialogTitle>
          <p className="text-[11.5px] text-neutral-600">
            A factory-floor view of your last 30 days: volume throughput,
            shelf-wide voice consistency, and the prompt-completion signal
            that predicts chapter quality before Claude drafts a word.
          </p>
        </DialogHeader>

        <div className="max-h-[75vh] overflow-y-auto p-5">
          {loading && (
            <div className="flex justify-center py-8" data-testid="studio-dashboard-loading">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          )}

          {!loading && data && (
            <>
              <div className="grid gap-3 md:grid-cols-3">
                <Card
                  icon={TrendingUp}
                  title="Volume Throughput"
                  primary={bulk.completed_novels ?? 0}
                  subtitle={`novels · ${bulk.runs || 0} wizard runs`}
                  testId="studio-card-throughput"
                >
                  <div className="mt-2 text-[10.5px] text-muted-foreground">
                    {(bulk.total_words || 0).toLocaleString()} words drafted end-to-end
                  </div>
                  <Sparkline series={bulk.series || []} />
                  {/* iter-studio-dashboard — "Draft next batch" CTA. */}
                  <button
                    type="button"
                    onClick={() => {
                      const fid = bulk.best_framework?.framework_id || '';
                      setOpen(false);
                      try {
                        window.dispatchEvent(new CustomEvent('bulk-novel-wizard-open',
                          { detail: { framework_id: fid } }));
                      } catch { /* ignore */ }
                    }}
                    className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-sm bg-oxblood-700 px-2 py-1 text-[11px] font-semibold text-white hover:bg-oxblood-800"
                    data-testid="studio-draft-next-batch"
                    title={bulk.best_framework?.framework_id
                      ? `Preload with your best framework: ${bulk.best_framework.name}`
                      : 'Open the Bulk Novel Wizard'}
                  >
                    <Sparkles className="h-3 w-3" />
                    Draft next batch
                    {bulk.best_framework?.framework_id && (
                      <span className="opacity-80">· {bulk.best_framework.name}</span>
                    )}
                  </button>
                  <HelpTip
                    slug="bulk-novel-wizard"
                    title="Bulk Novel Wizard"
                    content={<>Queue up to five full-length novels in parallel from one framework. Premier-only. Uses your character bibles + starter template to draft chapter-by-chapter — you review, then publish.</>}
                  >
                    <span data-testid="bulk-wizard-help-anchor" className="sr-only">bulk-wizard-help</span>
                  </HelpTip>
                </Card>

                <Card
                  icon={ShieldCheck}
                  title="Voice Consistency"
                  primary={voice.sample_size ? `${voice.avg_score}/100` : '—'}
                  subtitle={voice.sample_size
                    ? `avg across ${voice.sample_size} book${voice.sample_size === 1 ? '' : 's'}`
                    : 'no preflights yet'}
                  tone={voiceTone}
                  testId="studio-card-voice"
                >
                  <div className="mt-2 flex gap-3 text-[10.5px]">
                    <div className="flex items-center gap-1 text-emerald-700">
                      <ShieldCheck className="h-3 w-3" />
                      {voice.clean_book_count || 0} clean
                    </div>
                    <div className="flex items-center gap-1 text-amber-700">
                      <AlertTriangle className="h-3 w-3" />
                      {voice.drift_book_count || 0} flagged
                    </div>
                  </div>
                </Card>

                <Card
                  icon={ListChecks}
                  title="Prompt Completion"
                  primary={prompt.total_prompts ? `${prompt.overall_rate}%` : '—'}
                  subtitle={prompt.total_prompts
                    ? `avg across ${prompt.total_prompts} prompt${prompt.total_prompts === 1 ? '' : 's'}`
                    : 'no chapters drafted'}
                  tone={prompt.overall_rate >= 70 ? 'ok'
                    : prompt.overall_rate >= 40 || !prompt.total_prompts ? 'default'
                    : 'warn'}
                  testId="studio-card-prompt"
                >
                  {prompt.weakest_fields?.length > 0 && (
                    <ul className="mt-2 space-y-0.5 text-[10.5px] text-muted-foreground">
                      {prompt.weakest_fields.slice(0, 3).map((w) => (
                        <li key={w.field}
                            data-testid={`studio-weakest-${w.field.replace(/\./g, '-')}`}>
                          <span className="text-rose-600">{w.rate}%</span> {w.field}
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </div>

              {/* Full checklist below the KPI cards */}
              <div className="mt-4">
                <PromptQualityChecklist days={30} testId="studio-prompt-quality" />
              </div>

              {/* Empty-state hint */}
              {!bulk.completed_novels && !voice.sample_size && !prompt.total_prompts && (
                <div
                  className="mt-4 rounded-md border border-dashed border-oxblood-300 bg-oxblood-50/40 p-4 text-center text-[11.5px] text-neutral-700"
                  data-testid="studio-dashboard-empty"
                >
                  <Sparkles className="mx-auto h-5 w-5 text-oxblood-600" />
                  <p className="mt-1">
                    Nothing to show yet. Kick off a Bulk Novel run and draft a
                    chapter — this dashboard fills in automatically as your
                    studio starts producing.
                  </p>
                </div>
              )}
            </>
          )}

          {!loading && !data && (
            <div className="py-8 text-center text-[11.5px] text-muted-foreground"
                 data-testid="studio-dashboard-error">
              <BookText className="mx-auto h-5 w-5 text-muted-foreground" />
              <p className="mt-1">Could not load dashboard. Try again in a moment.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
